/**
 * Stage notes: a plant's stage recorded on a day, without a photograph.
 *
 * A plant's stage is its stage history: these, newest last (Grow's
 * automatic stages carry on from the newest; plantStage).
 *
 * Endpoints:
 * - POST   /stage-marks/:plantObjId { stageId, date, locationId? } → { success, mark } (locationId: D69, a vault plant's bed).
 * - POST   /stage-marks/:plantObjId/:markId/auto ("automatic again").
 * - DELETE /stage-marks/:plantObjId/:markId (the server puts a vault plant back in the vault when this mark moved it out, D69).
 *
 * Who uses it: features/plant-list (PlantStageLine, PlantFormDialog) and
 * features/today TodayPage. Nothing is cached here: a plant's marks arrive
 * inside the plants list, so callers refetch ['plants'] (and invalidate
 * 'plant-outlook') after a change. Auth: the signed-in token, in the
 * garden being tended.
 *
 * @module stageMarks
 */

import { apiClient } from './apiClient';

/** One stage recorded on a day: an entry in the plant's stage history. */
export interface StageMark {
  /** The entry's id. */
  mark_id: number;
  /** The stage recorded. */
  stage_id: number;
  /** The stage's name as the gardener sees it (their own, if renamed). */
  name: string;
  /** The calendar day, YYYY-MM-DD. */
  marked_on: string;
}

/**
 * Records a stage for a plant from a day.
 *
 * @param plantObjId - The plant.
 * @param stageId - The stage it reached.
 * @param date - The calendar day, YYYY-MM-DD, in the gardener's own time.
 * @param locationId - D69: for a plant in the Seed vault, the bed it went into (the server moves it there first); left
 *   out for Indoors and for any other plant.
 * @returns The new entry.
 */
export async function addStageMark(plantObjId: number, stageId: number, date: string, locationId?: number | null): Promise<StageMark> {
  const response = await apiClient.post<{ success: boolean; mark: StageMark }>(`/stage-marks/${plantObjId}`, { stageId, date, ...(locationId ? { locationId } : {}) });
  return response.data.mark;
}

/**
 * "Automatic again": Grow's automatic stages carry on from this entry, which,
 * as a correction of the gardener's, had stopped them. Nothing is removed.
 *
 * @param plantObjId - The plant.
 * @param markId - The entry, the newest in its history.
 */
export async function autoAgain(plantObjId: number, markId: number): Promise<void> {
  await apiClient.post(`/stage-marks/${plantObjId}/${markId}/auto`);
}

/**
 * Takes a recorded stage back off a plant's history.
 *
 * @param plantObjId - The plant.
 * @param markId - The entry to remove.
 */
export async function removeStageMark(plantObjId: number, markId: number): Promise<void> {
  await apiClient.delete(`/stage-marks/${plantObjId}/${markId}`);
}
